import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACK_RETRY_DELAYS_MS,
  PAYBACK_CHANGED_EVENT,
  isClaimable,
} from './sellerPlanPayback';

const MIN_REFRESH_GAP_MS = 30000;
const FALLBACK_RETRY_MS = 60000;

const INITIAL_DEBUG = {
  phase: 'idle',
  leaseHeld: false,
  leaseExpiresAt: null,
  ack: 'none',
  lastError: null,
  sessionHandled: [],
};

/**
 * Reads the payback snapshot for one channel and, with `autoClaim`, runs the popup lifecycle
 * (PLAN-SELLER-PLAN-PAYBACK-MILESTONE Phase 4a, R4 contract):
 *
 * - claim a server lease, renew it every `heartbeatSeconds` while the tab is visible and on return
 *   to the tab; a 409 on renewal means another device owns it, so close silently;
 * - closing acknowledges: the popup hides at once, a transient failure retries in this session
 *   without reopening, a 409 `lease-superseded` closes silently;
 * - everything async belongs to one scope per `(client, channelId)`; when the channel changes or the
 *   component unmounts the scope goes inactive and any late response is dropped, so a response for
 *   a previous channel can never display or acknowledge the new channel's milestone.
 *
 * `client` must be referentially stable. The lease token lives only in the scope, never in
 * rendered state, storage or logs.
 */
export default function useSellerPlanPayback({ client, channelId, autoClaim = false }) {
  const [state, setState] = useState({ status: 'idle', payback: null });
  const [popup, setPopup] = useState(null);
  const [debug, setDebug] = useState(INITIAL_DEBUG);
  const actions = useRef({});

  useEffect(() => {
    actions.current = {};
    if (!client || !channelId) {
      setState({ status: 'idle', payback: null });
      setPopup(null);
      setDebug(INITIAL_DEBUG);
      return undefined;
    }

    // A new scope never inherits the previous channel's snapshot or popup.
    setState({ status: 'loading', payback: null });
    setPopup(null);
    setDebug(INITIAL_DEBUG);

    const s = {
      active: true,
      lease: null,
      pendingAck: null,
      handled: new Map(),
      retryAfter: new Map(),
      heartbeat: null,
      ackTimer: null,
      lastRefreshAt: 0,
      refreshing: false,
    };
    const note = patch => {
      if (!s.active) return;
      setDebug(previous => ({
        ...previous,
        ...patch,
        sessionHandled: [...s.handled.entries()].map(([uuid, how]) => `${uuid.slice(0, 8)}:${how}`),
      }));
    };
    const announce = () => window.dispatchEvent(new CustomEvent(PAYBACK_CHANGED_EVENT));

    const stopHeartbeat = () => {
      if (s.heartbeat) clearInterval(s.heartbeat);
      s.heartbeat = null;
    };

    // Another device owns the milestone (or it is no longer claimable): drop the popup without
    // acknowledging. The card and the server state are the record.
    const closeSilently = (reason) => {
      stopHeartbeat();
      if (s.lease) s.handled.set(s.lease.uuid, 'skipped');
      s.lease = null;
      setPopup(null);
      note({ phase: `closed (${reason})`, leaseHeld: false, leaseExpiresAt: null });
    };

    const beat = async () => {
      const lease = s.lease;
      if (!lease || lease.beating || document.hidden) return;
      lease.beating = true;
      const result = await client.claim(lease.uuid, lease.token);
      lease.beating = false;
      if (!s.active || s.lease !== lease) return;
      if (result.ok) {
        lease.expiresAt = result.lease.expiresAt;
        note({ leaseExpiresAt: result.lease.expiresAt, lastError: null });
      } else if (result.status === 409) {
        closeSilently(result.reason || 'conflict');
      } else {
        // Transient: keep showing, the next beat or return to the tab retries.
        note({ lastError: `renewal ${result.reason || result.status}` });
      }
    };

    const startHeartbeat = heartbeatMs => {
      stopHeartbeat();
      s.heartbeat = setInterval(beat, heartbeatMs);
    };

    const sendAck = async (uuid, token, attempt) => {
      note({ ack: attempt ? `retry ${attempt}` : 'sending' });
      const result = await client.acknowledge(uuid, token);
      if (!s.active) return;
      if (result.ok) {
        s.pendingAck = null;
        setState(previous => ({ ...previous, payback: result.payback ?? previous.payback }));
        note({ ack: result.outcome || 'done', lastError: null });
        announce();
        return;
      }
      if (!result.transient) {
        // lease-superseded, not-claimable, not found: nothing more this client can do.
        s.pendingAck = null;
        note({ ack: `closed (${result.reason || result.status})` });
        announce();
        return;
      }
      if (attempt >= ACK_RETRY_DELAYS_MS.length) {
        s.pendingAck = null;
        note({ ack: 'gave up (card still shows it)' });
        return;
      }
      note({ ack: `failed, retrying in ${ACK_RETRY_DELAYS_MS[attempt] / 1000}s` });
      s.ackTimer = setTimeout(() => sendAck(uuid, token, attempt + 1), ACK_RETRY_DELAYS_MS[attempt]);
    };

    const dismiss = () => {
      const lease = s.lease;
      setPopup(null);
      if (!lease) return;
      s.lease = null;
      stopHeartbeat();
      s.handled.set(lease.uuid, 'dismissed');
      s.pendingAck = { uuid: lease.uuid, token: lease.token };
      note({ leaseHeld: false, leaseExpiresAt: null });
      sendAck(lease.uuid, lease.token, 0);
    };

    const present = async (milestone) => {
      note({ phase: 'claiming' });
      const result = await client.claim(milestone.uuid, null);
      if (!s.active) {
        if (result.ok) client.release(milestone.uuid, result.lease.token, { keepalive: true });
        return;
      }
      if (result.ok) {
        const served = result.payback?.milestone;
        const shown = served && served.uuid === milestone.uuid && served.claimable ? served : milestone;
        s.lease = {
          uuid: milestone.uuid,
          token: result.lease.token,
          expiresAt: result.lease.expiresAt,
          beating: false,
        };
        setPopup(shown);
        startHeartbeat(result.lease.heartbeatMs);
        note({ phase: 'presenting', leaseHeld: true, leaseExpiresAt: result.lease.expiresAt, ack: 'none' });
        return;
      }
      if (result.reason === 'contended') {
        // Another device is showing it. Try again on a later refresh once its lease could be gone.
        s.retryAfter.set(milestone.uuid, Date.now() + (result.retryAfterSeconds || 60) * 1000);
        note({ phase: 'contended (another device)', lastError: null });
      } else if (result.status === 409) {
        s.handled.set(milestone.uuid, 'skipped');
        note({ phase: `not shown (${result.reason || 'conflict'})` });
      } else {
        s.retryAfter.set(milestone.uuid, Date.now() + FALLBACK_RETRY_MS);
        note({ phase: 'claim failed', lastError: `claim ${result.reason || result.status}` });
      }
    };

    const refresh = async ({ force = false } = {}) => {
      if (!s.active || s.refreshing) return;
      const now = Date.now();
      if (!force && now - s.lastRefreshAt < MIN_REFRESH_GAP_MS) return;
      s.refreshing = true;
      s.lastRefreshAt = now;
      note({ phase: 'reading' });
      const result = await client.read();
      s.refreshing = false;
      if (!s.active) return;
      if (!result.ok) {
        setState(previous => ({ ...previous, status: 'error' }));
        note({ phase: 'read failed', lastError: `read ${result.reason || result.status}` });
        return;
      }
      setState({ status: 'ready', payback: result.payback });
      note({ phase: 'idle', lastError: null });
      const milestone = result.payback?.milestone;
      if (s.lease && milestone?.uuid === s.lease.uuid && !milestone.claimable) {
        closeSilently('acknowledged elsewhere');
        return;
      }
      if (!autoClaim || s.lease || s.pendingAck || !isClaimable(result.payback)) return;
      if (s.handled.has(milestone.uuid)) return;
      if ((s.retryAfter.get(milestone.uuid) || 0) > Date.now()) return;
      await present(milestone);
    };

    const onFocus = () => {
      refresh();
    };
    const onVisible = () => {
      if (document.hidden) return;
      beat();
      refresh();
    };
    const onChanged = () => {
      refresh({ force: true });
    };
    const onPageHide = () => {
      if (s.lease) client.release(s.lease.uuid, s.lease.token, { keepalive: true });
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener(PAYBACK_CHANGED_EVENT, onChanged);
    document.addEventListener('visibilitychange', onVisible);
    actions.current = { dismiss, refresh };
    refresh({ force: true });

    return () => {
      s.active = false;
      stopHeartbeat();
      if (s.ackTimer) clearTimeout(s.ackTimer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener(PAYBACK_CHANGED_EVENT, onChanged);
      document.removeEventListener('visibilitychange', onVisible);
      // Best effort only: finish a dismissal that was still retrying, or hand the lease back.
      if (s.pendingAck) client.acknowledge(s.pendingAck.uuid, s.pendingAck.token);
      else if (s.lease) client.release(s.lease.uuid, s.lease.token, { keepalive: true });
      actions.current = {};
    };
  }, [client, channelId, autoClaim]);

  const dismiss = useCallback(() => actions.current.dismiss?.(), []);
  const refresh = useCallback(() => actions.current.refresh?.({ force: true }), []);

  return { status: state.status, payback: state.payback, popup, dismiss, refresh, debug };
}
